import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useQueryParam, NumberParam, StringParam } from "use-query-params";
import { clientService } from "../../../../../../api";
import { Table, TableSkeleton, TopProgress, Typography } from "../../../../../../components";
import { timeConverterMMDDYYHHMM } from "../../../../../../utils/dates";
import { useTheme } from "styled-components";
import Skeleton from "react-loading-skeleton";
import { useUi } from "../../../../../../context/ui.context";
import {
  Container,
  DetailsContainer,
  DetailsRow,
  DetailsLabel,
  DetailsValue,
} from "./styles";

const ClientLog: FC = () => {
  const [t] = useTranslation();
  const theme: any = useTheme();
  const { userId } = useParams();
  const { viewAlert } = useUi();

  const [page] = useQueryParam("page", NumberParam);
  const [ordering] = useQueryParam("ordering", StringParam);
  const [pageSize] = useQueryParam("page_size", NumberParam);

  const [logs, setLogs] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<any>();
  const [pageLoading, setPageLoading] = useState<boolean>(true);

  const parseParameters = useCallback((raw?: string) => {
    if (!raw) return null;
    try {
      const normalized = raw.replace(/'/g, '"');
      const parsed = JSON.parse(normalized);
      return parsed;
    } catch (e) {
      return raw;
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        title: t("MESSAGE"),
        key: "message",
      },
      {
        title: t("TASKS"),
        key: "tasks",
      },
      {
        title: t("ENTRY_DATE"),
        key: "created_at",
      },
    ],
    [t]
  );

  const fetchLogs = () => {
    if (!userId) return;
    clientService
      .getClientLogs({
        page: page || 1,
        pageSize: pageSize || 20,
        ordering: ordering || "-created_at",
        client: userId,
      })
      .then((res) => {
        setLogs(
          res.data.objects.map((item: any) => {
            const parsedParameters = parseParameters(item.parameters);
            return {
              ...item,
              message: item.log,
              tasks:
                item.tasks_count && item.tasks_count > 0
                  ? t("TASKS_COUNT", { count: item.tasks_count })
                  : t("NO_TASKS"),
              created_at: timeConverterMMDDYYHHMM(item.created_at),
              hasHistory: Boolean(item.details || parsedParameters || item.operations?.length),
              customBody: (
                <DetailsContainer>
                  <DetailsRow>
                    <DetailsLabel>{t("STATE")}</DetailsLabel>
                    <DetailsValue>{item.state || "-"}</DetailsValue>
                  </DetailsRow>
                  {item.ip ? (
                    <DetailsRow>
                      <DetailsLabel>{t("IP_ADDRESS")}</DetailsLabel>
                      <DetailsValue>{item.ip}</DetailsValue>
                    </DetailsRow>
                  ) : null}
                  {item.user?.email ? (
                    <DetailsRow>
                      <DetailsLabel>{t("USER")}</DetailsLabel>
                      <DetailsValue>{item.user.email}</DetailsValue>
                    </DetailsRow>
                  ) : null}
                  {item.details ? (
                    <DetailsRow>
                      <DetailsLabel>{t("DETAILS")}</DetailsLabel>
                      <DetailsValue>{item.details}</DetailsValue>
                    </DetailsRow>
                  ) : null}
                  {parsedParameters && typeof parsedParameters === "object" ? (
                    <DetailsRow>
                      <DetailsLabel>{t("PARAMETERS")}</DetailsLabel>
                      <DetailsValue>
                        {Object.entries(parsedParameters).map(([key, value]) => (
                          <div key={key}>
                            <strong>{key}:</strong> {String(value)}
                          </div>
                        ))}
                      </DetailsValue>
                    </DetailsRow>
                  ) : parsedParameters ? (
                    <DetailsRow>
                      <DetailsLabel>{t("PARAMETERS")}</DetailsLabel>
                      <DetailsValue>{parsedParameters}</DetailsValue>
                    </DetailsRow>
                  ) : null}
                </DetailsContainer>
              ),
            };
          })
        );
        setPaginationData(res.data);
      })
      .catch((e: any) => {
        if (e?.response?.data?.detail) {
          viewAlert({
            severity: "error",
            message: e?.response?.data?.detail,
          });
        }
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  useEffect(() => {
    setPageLoading(true);
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, ordering, userId]);

  if (pageLoading) {
    return (
      <Container>
        <TopProgress loading />
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width={140}
          height={30}
          style={{ marginBottom: "12px" }}
        />
        <TableSkeleton rows={6} columns={columns.length} />
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h2" customStyles={{ marginBottom: "12px" }}>
        {t("LOG")}
      </Typography>
      <Table
        columns={columns}
        rows={logs}
        hideCheckbox
        hasDroppedRow
        paginationData={paginationData}
      />
    </Container>
  );
};

export default ClientLog;
